/** 
 * @module PlanVerificationPage
 * @description Renders the plan verification landing page list with server side pagination, sorting, and filtering.
 */
import { useEffect, useMemo, useContext, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AbsModal from "../../../common/absModal/AbsModal";
import {
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import "./PlanVerificationPage.scss";
import Loader from "../../../common/Loader/Loader";
import BreadCrumb from "../../../common/breadCrumb/BreadCrumb";
import { Button, CheckboxInput, Icon, Label, RadioInput } from "abs-ui-components";
import { accessAPIForResponse } from "../../../services/api-services/api-implementations";
import debounce from "lodash/debounce";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { PlanVerificationPageColumns } from "./PlanVerificationPageColumns";
import { MonitoringPlanConstant } from "../../../constants/MonitoringPlanConstant";
import axiosHttp from "../../../services/interceptor";
import { getMonitoringPlanFlags } from "../../../services/MonitoringPlanService";
import { ClaimsContext } from "../../../services/ClaimsService";
import { FeatureType } from "../../../constants/FeatureToggles";
import AutocompleteDropdown from "../../../common/autocompleteDropdown/AutocompleteDropdown";
import { useSelector } from "react-redux";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import ClearIcon from "@mui/icons-material/Clear";

const EMPTY_ARRAY = Object.freeze([]);

/**
 * Renders the plan verification list and handles related actions/modals.
 */
const PlanVerificationPage = () => {
  /* ----------------------------- State ----------------------------- */
  const [planData, setPlanData] = useState([]);
  const [planTypeListRowSelection, setPlanTypeListRowSelection] = useState({});
  const [loading, setLoading] = useState(true);
  const [pageLoader, setPageLoader] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  // Table state for server-side control
  const [globalFilter, setGlobalFilter] = useState("");
  const [sort, setSort] = useState([]);
  const [columnFilters, setColumnFilters] = useState(EMPTY_ARRAY);
  const [pendingColumnFilters, setPendingColumnFilters] = useState(EMPTY_ARRAY);
  const [serverColumnFilters, setServerColumnFilters] = useState(EMPTY_ARRAY);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [previousGlobalFilter, setPreviousGlobalFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Create plan modal state
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [planType, setPlanType] = useState("");
  const [selectedParentRecord, setSelectedParentRecord] = useState(null);
  const [shouldClone, setShouldClone] = useState(false);
  const [flags, setFlags] = useState(null);

  // Assign modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [statusActionBtnName, setStatusActionBtnName] = useState("");
  const [selectedEngineer, setSelectedEngineer] = useState("");
  const [disableAssignBtn, setDisableAssignBtn] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  // Header/footer (breadcrumb) visibility with debounced setter
  const [showBreadCrumb, setShowBreadCrumb] = useState(() => {
    const storedValue = sessionStorage.getItem("header-footer");
    return storedValue !== null ? JSON.parse(storedValue) : true;
  });
  const debouncedSetHeaderFooterEnabled = debounce((value) => {
    setShowBreadCrumb(value);
  }, 100);

  // Tracking active requests to prevent duplicates
  const activeRequestsRef = useRef(0);
  const lastQueryKeyRef = useRef("");
  const requestSeqRef = useRef(0);

  const [isPlanVerificationEnabled, setIsPlanVerificationEnabled] = useState(false);

  /* ---------------------------- Context ---------------------------- */
  const { userHasFeature, userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta } =
    useContext(ClaimsContext);
  const user = useSelector((state) => state.userInfo.userData);

  /* --------------------------- Routing ----------------------------- */
  const navigate = useNavigate();
  const location = useLocation();
  const wcnNumber = location.state?.wcnNumber;
  const currentUserEmail = user?.email;

  /* ------------------------ Data Fetchers ------------------------- */

  /**
   * Server-side table loader for plan verification list.
   * Applies global filter, column filters, pagination, and sorting.
   */
  const getAllPlans = useCallback(
    async (
      columnFilterPayload,
      searchText = "",
      pageIndex = 0,
      pageSize = 25,
      sortOptions = {
        sortField: "",
        isAscending: false,
      },
      options = { force: false } // allow callers to force a refresh
    ) => {
      const normalizedSort = sortOptions
        ? {
            SortField: sortOptions.SortField ?? sortOptions.sortField ?? "",
            IsAscending:
              typeof sortOptions.IsAscending === "boolean"
                ? sortOptions.IsAscending
                : !!sortOptions.isAscending,
          }
        : undefined;

      const filtersArray = columnFilterPayload?.map((filter) => ({
        key: filter.key,
        value: filter?.value,
      }));

      const requestPayload = {
        globalFilter: searchText,
        filters: filtersArray,
        pagination: { page: pageIndex + 1, pageSize },
        sortOption: normalizedSort,
      };

      const queryKey = JSON.stringify({
        g: requestPayload.globalFilter || "",
        f: requestPayload.filters || [],
        p: requestPayload.pagination,
        s: requestPayload.sortOption || {},
      });

      // Skip if identical request is in-flight unless forced
      if (
        activeRequestsRef.current > 0 &&
        lastQueryKeyRef.current === queryKey &&
        !options?.force
      ) {
        return;
      }

      lastQueryKeyRef.current = queryKey;

      if (activeRequestsRef.current === 0) setLoading(true);
      activeRequestsRef.current += 1;

      const mySeq = ++requestSeqRef.current;

      try {
        const response = await accessAPIForResponse("GetPlanVerificationList", requestPayload);
        if (mySeq !== requestSeqRef.current) return;

        if (response?.Data) {
          groupChildPlanTypesByVesselImo(response?.Data);
          setRowCount(response?.Pagination?.TotalItems);
          setPlanTypeListRowSelection({});
        }
      } finally {
        activeRequestsRef.current -= 1;
        if (activeRequestsRef.current === 0) setLoading(false);
      }
    },
    []
  );

  /**
   * Groups plans into parent/child structure based on IMO/WCN.
   * Ensures a synthetic parent when only child records exist.
   */
  const groupChildPlanTypesByVesselImo = (planTypes) => {
    const groups = new Map();

    for (const pt of planTypes || []) {
      const key = `id:${pt.ImoNumber}-${pt.WcnNumber}`;
      const group = groups.get(key) || { parent: null, childPlanTypes: [] };

      if (pt.IsParentRecord) {
        group.parent = pt;
      } else {
        group.childPlanTypes = [...group.childPlanTypes, pt];
      }
      groups.set(key, group);
    }

    const buildParentFromChild = (child) => {
      if (!child) return null;
      return {
        ImoNumber: child.ImoNumber,
        VesselName: child.VesselName,
        WcnNumber: child.WcnNumber,
        CompanyName: child.CompanyName,
        ImoVesselTypeName: child.ImoVesselTypeName,
        MrvVesselTypeName: child.MrvVesselTypeName,
        Flag: child.Flag,
        IsParentRecord: true,
        PlanId: `parent-${child.ImoNumber}-${child.WcnNumber}`,
        ShowCreateButton: child.ShowCreateButton ?? false,
      };
    };

    const nestedPlanTypeList = Array.from(groups.values()).map(({ parent, childPlanTypes }) => {
      const saneParent = parent ?? buildParentFromChild(childPlanTypes[0]);
      return {
        ...saneParent,
        childPlanTypes,
      };
    });

    setPlanData(nestedPlanTypeList);
  };

  /**
   * Row click handler: navigates to plan details unless clicking action column/parent row.
   */
  const handleReportListTableRowClick = (row, column) => {
    if (column.id !== "actionBtnName" && !row.original.IsParentRecord) {
      navigate(`/plan-verification/${row.original.PlanId}`, {
        state: { wcnNumber: row.original.WcnNumber },
      });
    }
  };

  /**
   * Fetches reviewer engineers for assignment modal.
   */
  const GetEngineersList = async (input) => {
    try {
      const resp = await axiosHttp.get("api/PlanVerification/GetReviewerEngineers", {
        params: { searchTerm: input?.trim() || null },
      });
      const list = Array.isArray(resp?.data?.data) ? resp.data.data : [];
      const isSuccess = resp?.data?.isSuccess === true;
      if (!isSuccess || !list.length) return [];
      return list
        .map((obj, index) => ({
          label: obj.emailAddress,
          value: obj.emailAddress,
          id: index + 1,
        }));
    } catch (error) {
      console.error("Error fetching engineers:", error);
      return [];
    }
  };

  /**
   * Fetches plan monitoring flags for clone guidance (FEU from MRV).
   */
  const handleMonitoringFlags = async () => {
    try {
      const res = await getMonitoringPlanFlags(selectedParentRecord.VesselMstId);
      if (res?.isSuccess) {
        setFlags(res.data);
      } else {
        setFlags(null);
      }
    } catch (err) {
      console.error("Failed to fetch flags:", err);
      setFlags(null);
    }
  };

  const existingPlanTypes = useMemo(
    () =>
      new Set(
        (selectedParentRecord?.childPlanTypes || []).map((c) => c.Plan).filter(Boolean)
      ),
    [selectedParentRecord]
  );

  const hasSeempIIPlan = existingPlanTypes.has(MonitoringPlanConstant.SEEMP_II);
  const hasSeempIIIPlan = existingPlanTypes.has(MonitoringPlanConstant.SEEMP_III);
  const hasMrvPlan = existingPlanTypes.has(MonitoringPlanConstant.MRV);
  const hasFuelEuPlan = existingPlanTypes.has(MonitoringPlanConstant.FuelEU);

  /* ---------------------------- Effects ---------------------------- */

  // Ensure user feature toggles are loaded and WCN aware
  useEffect(() => {
    if (!userFeaturesAreLoaded || checkWcnDelta(wcnNumber)) {
      fetchUserFeatures(wcnNumber);
    }
  }, [userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta, wcnNumber]);

  // Initialize header/footer state from session storage (debounced)
  useEffect(() => {
    const storedValue = sessionStorage.getItem("header-footer");
    if (storedValue !== null) {
      debouncedSetHeaderFooterEnabled(JSON.parse(storedValue));
    }
  }, [debouncedSetHeaderFooterEnabled]);

  // Fetch table data when server side inputs change
  useEffect(() => {
    const planVerificationEnabled =
      userFeaturesAreLoaded && userHasFeature(FeatureType.PlanVerification);
    if (!planVerificationEnabled) return;

    const columnFilterPayload = serverColumnFilters?.map((element) => ({
      key: element.id,
      value: element?.value,
    }));

    let sortOption;
    if (sort.length > 0) {
      sortOption = {
        SortField: sort[0].id === "WcnNumber" ? "WCN" : sort[0].id,
        IsAscending: !sort[0].desc,
      };
    }

    // Reset to first page on global filter change
    if (previousGlobalFilter !== globalFilter) {
      setPreviousGlobalFilter(globalFilter);
      if (pagination.pageIndex !== 0) {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        return;
      }
    }

    getAllPlans(
      columnFilterPayload,
      globalFilter,
      pagination.pageIndex,
      pagination.pageSize,
      sortOption
    );
  }, [
    userFeaturesAreLoaded,
    userHasFeature,
    serverColumnFilters,
    sort,
    globalFilter,
    previousGlobalFilter,
    pagination.pageIndex,
    pagination.pageSize,
    getAllPlans,
  ]);

  // Fetch MRV/FEU cloning flags when a parent record is selected
  useEffect(() => {
    if (selectedParentRecord?.VesselMstId) {
      handleMonitoringFlags();
    }
  }, [selectedParentRecord?.VesselMstId]);

  // Fetch feature toggle flag
  useEffect(() => {
    if (!userFeaturesAreLoaded) return;
    const enabled = userHasFeature(FeatureType.PlanVerification);
    setIsPlanVerificationEnabled(enabled);
  }, [userFeaturesAreLoaded, userHasFeature, wcnNumber]);

  // Derived plan existence flags
  useEffect(() => {
    if (showCreatePlanModal && planType && existingPlanTypes.has(planType)) {
      setPlanType("");
    }
  }, [showCreatePlanModal, existingPlanTypes, planType]);

useEffect(() => {
  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
}, [globalFilter]);
  /* --------------------------- Handlers --------------------------- */

  const handleCreatePlanButtonClick = (row) => {
    setSelectedParentRecord(row);
    setShowCreatePlanModal(!showCreatePlanModal);
  };

  const onChangePlanType = (type) => {
    setPlanType(type);
  };

  const handleEngineerSelect = (select) => {
    setSelectedEngineer(select?.value);
    setDisableAssignBtn(false);
  };

  const toggleAssignModalClose = () => {
    setShowAssignModal(false);
    setDisableAssignBtn(true);
    setSelectedEngineer("");
    setSelectedPlanId(null);
    setStatusActionBtnName("");
  };

  const handleCheckboxChange = (value) => {
    setShouldClone(value);
  };

  const toggleCreatePlanClose = () => {
    setShowCreatePlanModal(false);
    setPlanType("");
  };

  /**
   * Creates a new verification plan for the selected parent record.
   * Navigates to the newly created plan when successful.
   */
  const submitCreatePlanType = async () => {
    if (!planType || !selectedParentRecord) {
      return;
    }
    const requestPayload = {
      Plan: planType,
      VesselMstId: selectedParentRecord.VesselMstId,
      ImoNumber: selectedParentRecord.ImoNumber,
      VesselName: selectedParentRecord.VesselName,
      WcnNumber: selectedParentRecord.WcnNumber,
      CompanyName: selectedParentRecord.CompanyName,
      ImoVesselTypeName: selectedParentRecord.ImoVesselTypeName,
      MrvVesselTypeName: selectedParentRecord.MrvVesselTypeName,
      Flag: selectedParentRecord.Flag,
    };
    setLoading(true);
    try {
      const response = await axiosHttp.post(
        `api/PlanVerification/AddVerificationPlan`,
        requestPayload,
        { params: { wcnNumber: selectedParentRecord.WcnNumber } }
      );
      if (response) {
        emitToasterMsg("success", "Plan added successfully.");
        const getPlanId = (responseData, planType) => {
          const match = responseData.find(
            (item) =>
              !item.isParentRecord &&
              item.vesselMstId === selectedParentRecord.VesselMstId &&
              item.plan === planType
          );
          return match ? match.planId : null;
        };
        const planId = getPlanId(response.data, planType);
        if (planId) {
          navigate(`/plan-verification/${planId}`, {
            state: { shouldClone: shouldClone },
          });
        }
        getAllPlans(
          columnFilters?.map((element) => ({
            key: element.id,
            value: element?.value,
          })),
          globalFilter,
          pagination.pageIndex,
          pagination.pageSize,
          sort.length > 0
            ? sort.map((x) => ({
                SortField: x.id === "WcnNumber" ? "WCN" : x.id,
                IsAscending: !x.desc,
              }))[0]
            : undefined,
          { force: true } // ensure refresh after create
        );
      } else {
        emitToasterMsg(
          "error",
          "Error adding plan" || "An error occurred while adding the plan."
        );
      }
    } catch (error) {
      emitToasterMsg("error", "Error adding plan", error.message);
    }
    setShowCreatePlanModal(false);
    setPlanType("");
    setLoading(false);
  };

  /**
   * Assigns/Reassigns/Claims/Unclaims a verification plan.
   */
  const assignEngineerForPlan = async (engineer, btnName, planId) => {
    const endpoint =
      btnName === "Claim"
        ? "ClaimVerificationPlan"
        : btnName === "Unclaim" || btnName === "Un-Claim"
        ? "UnclaimVerificationPlan"
        : "AssignVerificationPlan";

    const planIdToUse = selectedPlanId ?? planId;

    try {
      const method = "GET";

      const response = await axiosHttp({
        url: `api/PlanVerification/${endpoint}`,
        method,
        params: {
          planId: planIdToUse,
          userName: engineer,
        },
      });

      if (response) {
        setDisableAssignBtn(true);
        setShowAssignModal(false);
        emitToasterMsg("success", "Plan assignment updated successfully.");

        const filters = (serverColumnFilters || []).map((f) => ({
          key: f.id,
          value: f?.value,
        }));

        const sortOption =
          sort.length > 0
            ? {
                SortField: sort[0].id === "WcnNumber" ? "WCN" : sort[0].id,
                IsAscending: !sort[0].desc,
              }
            : undefined;

        await getAllPlans(
          filters,
          globalFilter,
          pagination.pageIndex,
          pagination.pageSize,
          sortOption,
          { force: true } // ensure refresh after assignment
        );
      } else {
        console.error("Failed to update plan assignment.");
        emitToasterMsg("error", response?.message || "Failed to update plan assignment.");
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Opens assignment modal for Assign/Reassign, otherwise performs Claim/Unclaim.
   */
  const handleStatusModel = (btnName, planId, reviewer) => {
    if (btnName && btnName.toLowerCase().includes("assign")) {
      setSelectedEngineer(reviewer || "");
      setSelectedPlanId(planId);
      setStatusActionBtnName(btnName);
      setDisableAssignBtn(!reviewer);
      setShowAssignModal(true);
    } else {
      assignEngineerForPlan(user ? user?.email : null, btnName, planId);
    }
  };

  /* ------------------------ Feature Toggles ------------------------ */

  const showCreatePlanButton =
    userFeaturesAreLoaded && userHasFeature(FeatureType.AddPlanVerification);

  const isSeempIIIEnabled =
    userFeaturesAreLoaded && userHasFeature(FeatureType.CreateSeemp3MonitoringPlan);
  const isFuelEuEnabled =
    userFeaturesAreLoaded && userHasFeature(FeatureType.CreateFuelEuMonitoringPlan);
  const isMrvEnabled =
    userFeaturesAreLoaded && userHasFeature(FeatureType.CreateMrvMonitoringPlan);
  const isSeempIIEnabled =
    userFeaturesAreLoaded && userHasFeature(FeatureType.CreateSeemp2MonitoringPlan);

  /* -------------------- Table Column Overrides --------------------- */

  const columns = useMemo(() => {
    return PlanVerificationPageColumns.map((col) => {
      if (col.accessorKey === "VerificationStatus") {
        return {
          ...col,
          Cell: ({ row: { original } }) => {
            const statusText = original?.VerificationStatus ?? "";
            if (original.IsParentRecord) {
              return statusText;
            }
            const action = original.ShowStatusAction; // "Assign" | "Reassign" | "Claim" | "Unclaim" | null
            const buttonActions = new Set(["Assign", "Reassign", "Claim", "Unclaim"]);
            return (
              <div className="d-flex align-items-center">
                <span>{statusText}</span>
                {action &&
                  (buttonActions.has(action) ? (
                    <Button
                      className="ml-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusModel(action, original.PlanId, original.Reviewer);
                      }}
                    >
                      {action}
                    </Button>
                  ) : (
                    <Label className="ml-2" name={action} />
                  ))}
              </div>
            );
          },
        };
      }
      if (col.accessorKey === "Plan") {
        return {
          ...col,
          Cell: ({ row: { original } }) => {
            const canDownload =
              !original.IsParentRecord && original?.VerificationStatus === "Approved";

            const handleDownloadDocuments = async (e) => {
              e.stopPropagation();
              setPageLoader(true);
              try {
                const resp = await axiosHttp({
                  url: `api/PlanVerification/DownloadVerificationDocuments`,
                  method: "GET",
                  params: {
                    planId: original.PlanId,
                    wcnNumber: original.WcnNumber,
                  },
                  responseType: "blob",
                });

                const blob =
                  resp?.data instanceof Blob
                    ? resp.data
                    : new Blob([resp], {
                        type: resp?.type || "application/octet-stream",
                      });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");

                let filename = `VerificationDocuments_${original.ImoNumber}_${original.Plan}.zip`;
                try {
                  const cd =
                    resp?.headers?.["content-disposition"] ||
                    resp?.headers?.get?.("content-disposition");
                  const match = cd && /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd);
                  if (match && match[1]) {
                    filename = match[1].replace(/['"]/g, "");
                  }
                } catch {}

                link.href = url;
                link.setAttribute("download", filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                emitToasterMsg("success", "Documents downloaded successfully.");
              } catch (error) {
                console.error(error);
                emitToasterMsg("error", "Failed to download documents.");
              } finally {
                setPageLoader(false);
              }
            };

            return (
              <div className="d-flex align-items-center">
                {canDownload && (
                  <IconButton
                    size="small"
                    className="mr-2"
                    aria-label="Download documents"
                    onClick={handleDownloadDocuments}
                    title="Download documents"
                    sx={{ color: "#0645AD" }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                )}
                <span>{original.Plan}</span>
              </div>
            );
          },
        };
      }
      return col;
    });
  }, []);

  const applyFilters = useCallback(() => {

    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));

    setServerColumnFilters([
      ...columnFilters,
    ]);

    setGlobalFilter(searchInput);

  }, [columnFilters, searchInput]);

  const resetFilterForColumn = useCallback((columnId) => {

    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));

    setServerColumnFilters((prev) =>
      prev.filter((f) => f.id !== columnId)
    );

  }, []);

  const handleColumnFiltersChange = useCallback(
    (updater) => {

      setColumnFilters((prev) => {

        const updated =
          typeof updater === "function"
            ? updater(prev)
            : updater;

        // detect removed filters
        prev.forEach((oldFilter) => {

          const stillExists = updated.some(
            (newFilter) =>
              newFilter.id === oldFilter.id
          );

          if (!stillExists) {

            // ONLY if previously applied
            const wasApplied =
              serverColumnFilters.some(
                (f) => f.id === oldFilter.id
              );

            if (wasApplied) {
              resetFilterForColumn(oldFilter.id);
            }
          }
        });

        return updated;
      });
    },
    [serverColumnFilters, resetFilterForColumn]
  );

  const updateFilterValue = useCallback(
    (columnId, value) => {

      setColumnFilters((prev) => {

        const normalizedValue =
          value?.trim?.() ?? "";

        const others = prev.filter(
          (f) => f.id !== columnId
        );

        const updatedPending = normalizedValue
          ? [
            ...others,
            {
              id: columnId,
              value: normalizedValue,
            },
          ]
          : others;

        // ONLY auto reset if filter was already applied
        const wasApplied =
          serverColumnFilters.some(
            (f) => f.id === columnId
          );

        if (!normalizedValue && wasApplied) {

          setPagination((prev) => ({
            ...prev,
            pageIndex: 0,
          }));

          setServerColumnFilters((prevApplied) =>
            prevApplied.filter(
              (f) => f.id !== columnId
            )
          );
        }

        return updatedPending;
      });
    },
    [serverColumnFilters]
  );

  const clearAllFilters = useCallback(() => {

    setColumnFilters([]);

    setServerColumnFilters([]);

    setSearchInput("");

    setGlobalFilter("");

    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));

  }, []);
  
  /* ------------------------- Table ------------------------ */

  const table = useMaterialReactTable({
		columns: [
			...columns,
			{
				accessorKey: "actionBtnName",
				header: " ",
				enableSorting: false,
				enableColumnActions: false,
				enableColumnFilters: false,
				enableCellActions: true,
				Filter: () => null,
				size: 50,
				Cell: ({ row: { original } }) => {
					if (original.IsParentRecord && showCreatePlanButton) {
						const isEnabled = !!original?.ShowCreateButton;
						return (
							<Button
								disabled={!isEnabled}
								onClick={() => {
									if (isEnabled) handleCreatePlanButtonClick(original);
								}}
								className="mr-2 create-btn"
							>
								Create&nbsp;Plan
							</Button>
						);
					}
					if (!original.IsParentRecord && original.VerificationStatus === "Verification Complete") {
						const handleDownloadClick = async (event) => {
							event.stopPropagation();
							setPageLoader(true);

							await axiosHttp({
								url: `api/PlanVerification/DownloadVerificationPlan?planId=${encodeURIComponent(
									original.PlanId
								)}&wcnNumber=${encodeURIComponent(original.WcnNumber)}`,
								method: "GET",
							})
								.then((response) => {
									setPageLoader(false);
									const url = window.URL.createObjectURL(
										new Blob([response], {
											type: response?.type,
										})
									);
									const link = document.createElement("a");
									link.href = url;
									link.setAttribute("download", `VerificationPlan_${original.ImoNumber}_${original.Plan}.pdf`);
									document.body.appendChild(link);
									link.click();
									emitToasterMsg("success", "Verification plan downloaded successfully.");
								})
								.catch((error) => {
									setPageLoader(false);
								});
						};
						return (
							<div className="text-left">
								<Button outline={true} isIconOnly className="mr-2" variant="tertiary" onClick={handleDownloadClick}>
									<Icon icon="download" className="d-flex" outline={true} size="24px" />
								</Button>
							</div>
						);
					}
					return null;
				},
			},
		],
		data: planData,
		enableStickyHeader: true,
		rowCount,
		enableExpandAll: true,
		enableExpanding: true,
		manualPagination: true,
		enableBatchRowSelection: true,
		manualFiltering: true,
		manualSorting: true,
		enableSorting: false,
		filterFromLeafRows: true,
		enableTopToolbar: true,
		enableHiding: false,
		getSubRows: (row) => row.childPlanTypes,
		paginateExpandedRows: true,
		onPaginationChange: setPagination,
		onGlobalFilterChange: (value) => {
			setSearchInput(value ?? "");
			setPagination((prev) => ({ ...prev, pageIndex: 0 }));
		},
		onSortingChange: setSort,
		onColumnFiltersChange: handleColumnFiltersChange,
		getRowId: (row) => row.PlanId,
		onRowSelectionChange: setPlanTypeListRowSelection,
		state: {
			rowSelection: planTypeListRowSelection,
			pagination,
			globalFilter:searchInput,
			columnFilters,
			sorting: sort,
			showSkeletons: loading,
		},
		initialState: {
			density: "compact",
			expanded: true,
			columnOrder: [
				"actionBtnName",
				"Plan",
				"ImoNumber",
				"VesselName",
				"WcnNumber",
				"CompanyName",
				"ImoVesselTypeName",
				"MrvVesselTypeName",
				"Flag",
				"SubmissionDate",
				"VerifiedDate",
				"ModifiedDate",
				"Correction",
				"VerificationStatus",
				"Reviewer",
				"Verifier",
			],
		},
		muiSkeletonProps: { animation: "wave", width: 100 },
		muiTableBodyRowProps: ({ row }) => ({
			sx: { cursor: "pointer" },
		}),
		muiTableBodyCellProps: ({ row, row: { original }, column }) => ({
			ImoNumber: original.ImoNumber,
			column: column,
			sx: { fontWeight: row.original.IsParentRecord ? "bold" : "null" },
			onClick: () => handleReportListTableRowClick(row, column),
		}),
		muiPaginationProps: {
			rowsPerPageOptions: [25, 50, 75, 100],
			showFirstButton: true,
			showLastButton: true,
		},
		muiTableContainerProps: {
			sx: { maxHeight: "calc(100vh - 379px)" },
		},
		renderToolbarInternalActions: ({ table }) => (
			<>
				<MRT_ToggleGlobalFilterButton table={table} />
				<MRT_ToggleFiltersButton table={table} />
				<Button
					className="ml-2"
					onClick={() => {
						applyFilters();
					}}
				>
					Apply Filters
				</Button>
				<Button
					className="ml-2"
					variant="secondary"
					onClick={clearAllFilters}
				>
					Clear
				</Button>
			</>
		),
		muiTableHeadCellProps: ({ column }) => ({
			id: `${column.columnDef.accessorKey}`,
			sx: {
				color: "rgb(255 255 255)",
				"& .MuiButtonBase-root": {
					color: "white !important",
					"& .MuiTableSortLabel-icon": {
						color: "white !important",
					},
				},
			},
		}),
		muiFilterTextFieldProps: ({ column }) => ({
			sx: {
				"& .MuiInput-root": { color: "white !important" },
				"& .MuiButtonBase-root": { color: "white !important" },
				"& .MuiInputBase-root::after": {
					transform: "none",
					borderBottom: "2px solid white",
				},
			},
			onChange: (e) => {

				const value = e.target.value;

				updateFilterValue(column.id, value);
			},
			onKeyDown: (e) => {
				if (e.key === "Enter") {
					applyFilters();
				}
			},
		}),
		muiColumnActionsButtonProps: { sx: { color: "white" } },
		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",
				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},
			},
		},
		muiTablePaperProps: {
			sx: { boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)" },
		},

		muiSearchTextFieldProps: {
			placeholder: "Type and Enter",
			value: searchInput,
			onChange: (e) => {
				const value = e.target.value;
				setSearchInput(value);
        setGlobalFilter(value);
				setPagination((prev) => ({ ...prev, pageIndex: 0 }));

				if (value === "") {
					setGlobalFilter(""); // clear global filter when empty
				}
			},
			onKeyDown: (e) => {
				if (e.key === "Enter") {
          
					applyFilters();
				}
			},
		},
		muiSearchTextFieldProps: {
			placeholder: "Search",
			value: searchInput,
			onChange: (e) => {
				const value = e.target.value;
				setSearchInput(value);
        
				setPagination((prev) => ({ ...prev, pageIndex: 0 }));
				if (value === "") {
					setGlobalFilter("");
				}
			},
			onKeyDown: (e) => {
				if (e.key === "Enter") {
					setPagination((prev) => ({ ...prev, pageIndex: 0 }));
					setGlobalFilter(searchInput);
				}
			},
			InputProps: {
				endAdornment: searchInput && (
					<IconButton
						size="small"
						onClick={() => {
							setSearchInput("");
							setGlobalFilter("");
							setPagination((prev) => ({ ...prev, pageIndex: 0 }));
						}}
						sx={{ visibility: searchInput ? "visible" : "hidden" }}
					>
						<ClearIcon fontSize="small" />
					</IconButton>
				),
			},
		},
  });
   
  
  /* --------------------------- Breadcrumb -------------------------- */

  const breadcrumb = useMemo(
    () => [
      { label: "Annual Emission Verification", url: "", navId: "annual_emission_Verification_navId" },
      { label: "Plan Verification", url: "", navId: "plan_verification_navId" },
    ],
    []
  );

  /* ----------------------------- Render ---------------------------- */
  return (
    <div className="plan-verification-list-container p-4">
      {pageLoader && <Loader />}
      {showBreadCrumb && <BreadCrumb items={breadcrumb} />}

      <div className="mt-4 plan-verification-list-table">
        <MaterialReactTable table={table} />
      </div>

      {/* Create Plan Modal */}
      {showCreatePlanModal && (
        <AbsModal
          isOpen={showCreatePlanModal}
          title={`Create Plan`}
          onClose={setShowCreatePlanModal}
          close={toggleCreatePlanClose}
        >
          <div className="p-5">
            <div className="createbtn-radio-container pb-4">
              {isSeempIIEnabled && (
                <RadioInput
                  label={MonitoringPlanConstant.SEEMP_II}
                  gap="true"
                  checked={planType === MonitoringPlanConstant.SEEMP_II}
                  disabled={hasSeempIIPlan}
                  onChange={() => {
                    if (!hasSeempIIPlan) onChangePlanType(MonitoringPlanConstant.SEEMP_II);
                  }}
                />
              )}
              {isSeempIIIEnabled && (
                <RadioInput
                  label={MonitoringPlanConstant.SEEMP_III}
                  gap="true"
                  checked={planType === MonitoringPlanConstant.SEEMP_III}
                  disabled={hasSeempIIIPlan}
                  onChange={() => {
                    if (!hasSeempIIIPlan) onChangePlanType(MonitoringPlanConstant.SEEMP_III);
                  }}
                />
              )}
              {isMrvEnabled && (
                <RadioInput
                  label={MonitoringPlanConstant.MRV}
                  gap="true"
                  checked={planType === MonitoringPlanConstant.MRV}
                  disabled={hasMrvPlan}
                  onChange={() => {
                    if (!hasMrvPlan) onChangePlanType(MonitoringPlanConstant.MRV);
                  }}
                />
              )}
              {isFuelEuEnabled && (
                <RadioInput
                  label={MonitoringPlanConstant.FuelEU}
                  gap="true"
                  checked={planType === MonitoringPlanConstant.FuelEU}
                  disabled={hasFuelEuPlan}
                  onChange={() => {
                    if (!hasFuelEuPlan) onChangePlanType(MonitoringPlanConstant.FuelEU);
                  }}
                />
              )}
            </div>

            {(planType === MonitoringPlanConstant.FuelEU &&
              flags?.hasMrvPlan &&
              !flags?.hasFuelEuPlan) && (
              <div className="mt-3 ml-3">
                <Label
                  name="Please confirm the creation of the Fuel EU Monitoring Plan"
                  bold
                />
                <Label
                  name=" If you would like to replicate relevant sections from the existing MRV Monitoring Plan,
                                               select the checkbox below before proceeding."
                  bold
                />
                <div className="d-flex align-items-center mt-2">
                  <CheckboxInput checked={shouldClone} onChange={handleCheckboxChange} />
                  <Label
                    name="Clone from MRV Monitoring Plan"
                    bold
                    className="mb-0 ml-2"
                  />
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end mr-3 createPlan-model-btns">
              <Button className="mr-3" id="save-button" onClick={submitCreatePlanType} disabled={!planType}>
                Proceed
              </Button>
              <Button id="clear-button " onClick={toggleCreatePlanClose} variant="secondary">
                Cancel
              </Button>
            </div>
          </div>
        </AbsModal>
      )}

      {/* Assign/Reassign/Claim/Unclaim Modal */}
      {showAssignModal && (
        <AbsModal
          isOpen={showAssignModal}
          title={"Assignment"}
          onClose={setShowAssignModal}
          close={toggleAssignModalClose}
        >
          <div className="ml-3">
            <h4 className="blod">Assignment</h4>
            <hr />
            <p>Please select an Engineer</p>
            <hr />
            <AutocompleteDropdown
              className="inputHeight"
              id="search-engineer-id"
              isClearable
              loadOptions={async (inputText) => {
                const options = await GetEngineersList(inputText);
                return options;
              }}
              placeholder={selectedEngineer || "Select an Engineer"}
              autoFocus={false}
              onChange={handleEngineerSelect}
              customStyles={{
                menuList: (provided) => ({
                  ...provided,
                }),
              }}
            />
            <div className="d-flex justify-content-end mr-3 assignment-model-btns">
              <Button
                className="mr-3"
                id="assign-claim-btn-plan-list"
                onClick={() => {
                  assignEngineerForPlan(selectedEngineer, statusActionBtnName, selectedPlanId);
                }}
                disabled={disableAssignBtn}
              >
                Assign/Reassign
              </Button>
              <Button id="cancel-btn-plan-list" variant="secondary" onClick={toggleAssignModalClose}>
                Cancel
              </Button>
            </div>
          </div>
        </AbsModal>
      )}
    </div>
  );
};

export default PlanVerificationPage;
